import { Mesh, MeshStandardMaterial } from 'three';
import type JawCorrection from './JawCorrection';
import type ToothCorrection from './ToothCorrection';

const GHOST_COLOR = 0x7dd3fc;
const GHOST_OPACITY = 0.25;
/** Deltas below these are "not moved" — the ghost would just z-fight the live tooth. */
const MIN_POSITION_DELTA = 0.01; // mm
const MIN_ROTATION_DELTA_RAD = 0.1 * (Math.PI / 180);

/**
 * Semi-transparent copies of the teeth at their INITIAL (scan) pose, so the
 * user can see where a tooth moved from. Geometry is shared with the live
 * tooth — transforms never touch the render buffer — only the Object3D pose
 * differs. Ghosts are drawn only for teeth that actually moved, and refreshed
 * on transform end (see JawCorrection.onActiveToothTransformEnd).
 */
export default class InitialPoseGhosts {
  private readonly jaw: JawCorrection;
  private readonly ghosts = new Map<ToothCorrection, Mesh>();
  private visible = false;

  constructor(jaw: JawCorrection) {
    this.jaw = jaw;
  }

  public isVisible(): boolean {
    return this.visible;
  }

  public setVisible(visible: boolean) {
    this.visible = visible;
    if (visible) {
      this.refresh();
    } else {
      this.disposeGhosts();
    }
  }

  /** Re-syncs which teeth show a ghost. No-op while the toggle is off. */
  public refresh() {
    if (!this.visible) return;
    for (const tooth of this.jaw.teeth) {
      const moved = this.hasMoved(tooth);
      const existing = this.ghosts.get(tooth);
      if (moved && !existing) {
        this.ghosts.set(tooth, this.buildGhost(tooth));
      } else if (!moved && existing) {
        this.disposeGhost(existing);
        this.ghosts.delete(tooth);
      }
    }
  }

  public dispose() {
    this.disposeGhosts();
  }

  private hasMoved(tooth: ToothCorrection): boolean {
    if (tooth.getMovedPosition().length() > MIN_POSITION_DELTA) return true;
    const q = tooth.getMovedRotQuaternion();
    const w = Math.min(1, Math.abs(q.w));
    return 2 * Math.acos(w) > MIN_ROTATION_DELTA_RAD;
  }

  private buildGhost(tooth: ToothCorrection): Mesh {
    const material = new MeshStandardMaterial({
      color: GHOST_COLOR,
      roughness: 0.4,
      transparent: true,
      opacity: GHOST_OPACITY,
      depthWrite: false,
    });
    const ghost = new Mesh(tooth.geometry, material);
    ghost.name = `initial-pose-ghost-${tooth.getFDI()}`;
    ghost.position.copy(tooth.getInitialPosition());
    ghost.quaternion.copy(tooth.getInitialQuaternion());
    // Must never swallow the raycaster clicks that select the live tooth.
    ghost.raycast = () => {};
    ghost.renderOrder = 20; // under the contact overlay (30)
    tooth.parent?.add(ghost);
    return ghost;
  }

  private disposeGhosts() {
    for (const ghost of this.ghosts.values()) {
      this.disposeGhost(ghost);
    }
    this.ghosts.clear();
  }

  private disposeGhost(ghost: Mesh) {
    ghost.parent?.remove(ghost);
    (ghost.material as MeshStandardMaterial).dispose();
    // Geometry is the live tooth's — not ours to dispose.
  }
}
